/**
 * Pure TypeScript state management for AI Assistant Core
 *
 * No Vue / React / Pinia dependency.
 * Can be wrapped by any framework's reactive system.
 */

import type {
  AiOpenMode,
  AiThemeMode,
  AssistantConnection,
  AssistantMessage,
  AssistantPresentation,
  AssistantTurnPhase,
  AssistantVisibility,
  Conversation,
  MessageStatus,
  ToolStep,
} from '../protocol'
import {
  canonicalPresentationFromLegacy,
  resolveAssistantPresentation,
} from './presentation'

export * from './presentation'

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

export interface AssistantState {
  /** Canonical shell visibility. */
  visibility: AssistantVisibility
  /** User-selected layout; never overwritten by responsive behavior. */
  preferredPresentation: AssistantPresentation
  /** Layout currently rendered after responsive resolution. */
  resolvedPresentation: AssistantPresentation
  /** Previous preferred layout for back-navigation. */
  previousPresentation: AssistantPresentation | null
  /** User-visible connection state. */
  connection: AssistantConnection
  /** Current turn lifecycle. */
  turnPhase: AssistantTurnPhase
  /** Composer draft, retained across presentation changes and failures. */
  draft: string
  /** Last transcript position for restoration after a host remount. */
  scrollAnchor: { conversationId: string | null; top: number; pinned: boolean }
  /** Stable focus target id for presentation transitions. */
  focusedControlId: string | null
  /** Selected artifact id for the inspector. */
  selectedArtifactId: string | null
  /** Floating window position (px) */
  position: { x: number; y: number }
  /** Floating window size (px) */
  size: { width: number; height: number }
  /** Business system code */
  systemCode: string
  /** Selected model code */
  modelCode: string
  /** Current conversation id */
  currentConversationId: string | null
  /** All loaded conversations */
  conversations: Conversation[]
  /** Messages keyed by conversation id */
  messagesByConversation: Record<string, AssistantMessage[]>
  /** Tool steps keyed by message id */
  toolStepsByMessage: Record<string, ToolStep[]>
  /** Stream connection status */
  streamStatus: 'idle' | 'connecting' | 'streaming' | 'reconnecting' | 'closed' | 'error'
  /** Last received sequence number (for ack / resume) */
  lastSeq: number
  /** Last heartbeat timestamp */
  lastHeartbeatAt: number
  /** Reconnect attempt counter */
  reconnectAttempt: number
  /** Theme mode */
  themeMode: AiThemeMode
  /** Primary color */
  primaryColor: string
  /** Whether an error toast is currently shown */
  hasError: boolean
  /** Last error message (transient) */
  lastError: string | null
}

// ---------------------------------------------------------------------------
// Action types (discriminated union for reducer)
// ---------------------------------------------------------------------------

export type StateAction =
  | { type: 'OPEN'; presentation?: AssistantPresentation; mode?: AiOpenMode }
  | { type: 'CLOSE' }
  | { type: 'TOGGLE' }
  | { type: 'SET_VISIBILITY'; visibility: AssistantVisibility }
  | { type: 'SWITCH_PRESENTATION'; presentation: AssistantPresentation; viewportWidth?: number }
  | { type: 'RESOLVE_PRESENTATION'; viewportWidth: number }
  /** @deprecated Dispatch `SWITCH_PRESENTATION`. */
  | { type: 'SWITCH_MODE'; mode: AiOpenMode }
  | { type: 'SET_CONNECTION'; connection: AssistantConnection }
  | { type: 'SET_TURN_PHASE'; phase: AssistantTurnPhase }
  | { type: 'SET_DRAFT'; draft: string }
  | { type: 'SET_SCROLL_ANCHOR'; conversationId: string | null; top: number; pinned: boolean }
  | { type: 'SET_FOCUSED_CONTROL'; id: string | null }
  | { type: 'SELECT_ARTIFACT'; id: string | null }
  | { type: 'SET_POSITION'; x: number; y: number }
  | { type: 'SET_SIZE'; width: number; height: number }
  | { type: 'SET_SYSTEM_CODE'; systemCode: string }
  | { type: 'SET_MODEL_CODE'; modelCode: string }
  | { type: 'SET_CONVERSATION'; id: string | null }
  | { type: 'ADD_CONVERSATION'; conversation: Conversation }
  | { type: 'SET_CONVERSATIONS'; conversations: Conversation[] }
  | { type: 'APPEND_MESSAGE'; conversationId: string; message: AssistantMessage }
  | { type: 'APPEND_DELTA'; conversationId: string; messageId: string; delta: string }
  | { type: 'UPDATE_MESSAGE_STATUS'; conversationId: string; messageId: string; status: MessageStatus }
  | { type: 'SET_MESSAGES'; conversationId: string; messages: AssistantMessage[] }
  | { type: 'APPEND_TOOL_STEP'; messageId: string; step: ToolStep }
  | { type: 'UPDATE_TOOL_STEP'; messageId: string; stepId: string; patch: Partial<ToolStep> }
  | { type: 'SET_STREAM_STATUS'; status: AssistantState['streamStatus'] }
  | { type: 'SET_LAST_SEQ'; seq: number }
  | { type: 'SET_HEARTBEAT'; ts: number }
  | { type: 'INCREMENT_RECONNECT' }
  | { type: 'RESET_RECONNECT' }
  | { type: 'SET_THEME'; mode: AiThemeMode }
  | { type: 'SET_PRIMARY_COLOR'; color: string }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'HYDRATE'; state: Partial<AssistantState> }

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createInitialState(overrides?: Partial<AssistantState>): AssistantState {
  return {
    visibility: 'closed',
    preferredPresentation: 'compact',
    resolvedPresentation: 'compact',
    previousPresentation: null,
    connection: 'idle',
    turnPhase: 'idle',
    draft: '',
    scrollAnchor: { conversationId: null, top: 0, pinned: true },
    focusedControlId: null,
    selectedArtifactId: null,
    position: { x: 0, y: 0 },
    size: { width: 420, height: 600 },
    systemCode: '',
    modelCode: '',
    currentConversationId: null,
    conversations: [],
    messagesByConversation: {},
    toolStepsByMessage: {},
    streamStatus: 'idle',
    lastSeq: 0,
    lastHeartbeatAt: 0,
    reconnectAttempt: 0,
    themeMode: 'system',
    primaryColor: '#1677ff',
    hasError: false,
    lastError: null,
    ...overrides,
  }
}

// ---------------------------------------------------------------------------
// Reducer (pure function)
// ---------------------------------------------------------------------------

export function reducer(state: AssistantState, action: StateAction): AssistantState {
  switch (action.type) {
    case 'OPEN':
      if (action.mode) {
        const canonical = canonicalPresentationFromLegacy(action.mode)
        return {
          ...state,
          visibility: 'open',
          preferredPresentation: canonical.preferredPresentation,
          resolvedPresentation: canonical.preferredPresentation,
        }
      }
      return {
        ...state,
        visibility: 'open',
        preferredPresentation: action.presentation ?? state.preferredPresentation,
        resolvedPresentation: action.presentation ?? state.resolvedPresentation,
      }

    case 'CLOSE':
      return { ...state, visibility: 'closed' }

    case 'TOGGLE':
      return { ...state, visibility: state.visibility === 'open' ? 'closed' : 'open' }

    case 'SET_VISIBILITY':
      return { ...state, visibility: action.visibility }

    case 'SWITCH_PRESENTATION':
      return {
        ...state,
        visibility: 'open',
        previousPresentation: state.preferredPresentation,
        preferredPresentation: action.presentation,
        resolvedPresentation:
          typeof action.viewportWidth === 'number'
            ? resolveAssistantPresentation(action.presentation, action.viewportWidth)
            : action.presentation,
      }

    case 'RESOLVE_PRESENTATION':
      return {
        ...state,
        resolvedPresentation: resolveAssistantPresentation(
          state.preferredPresentation,
          action.viewportWidth
        ),
      }

    case 'SWITCH_MODE': {
      const canonical = canonicalPresentationFromLegacy(action.mode)
      return {
        ...state,
        visibility: canonical.visibility,
        previousPresentation: state.preferredPresentation,
        preferredPresentation: canonical.preferredPresentation,
        resolvedPresentation: canonical.preferredPresentation,
      }
    }

    case 'SET_CONNECTION':
      return { ...state, connection: action.connection }

    case 'SET_TURN_PHASE':
      return { ...state, turnPhase: action.phase }

    case 'SET_DRAFT':
      return { ...state, draft: action.draft }

    case 'SET_SCROLL_ANCHOR':
      return {
        ...state,
        scrollAnchor: {
          conversationId: action.conversationId,
          top: action.top,
          pinned: action.pinned,
        },
      }

    case 'SET_FOCUSED_CONTROL':
      return { ...state, focusedControlId: action.id }

    case 'SELECT_ARTIFACT':
      return { ...state, selectedArtifactId: action.id }

    case 'SET_POSITION':
      return {
        ...state,
        position: { x: action.x, y: action.y },
      }

    case 'SET_SIZE':
      return {
        ...state,
        size: { width: action.width, height: action.height },
      }

    case 'SET_SYSTEM_CODE':
      return { ...state, systemCode: action.systemCode }

    case 'SET_MODEL_CODE':
      return { ...state, modelCode: action.modelCode }

    case 'SET_CONVERSATION':
      return { ...state, currentConversationId: action.id }

    case 'ADD_CONVERSATION': {
      const next = [action.conversation, ...state.conversations]
      return { ...state, conversations: next }
    }

    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.conversations }

    case 'APPEND_MESSAGE': {
      const list = state.messagesByConversation[action.conversationId] ?? []
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [action.conversationId]: [...list, action.message],
        },
      }
    }

    case 'APPEND_DELTA': {
      const list = state.messagesByConversation[action.conversationId]
      if (!list) return state
      const idx = list.findIndex(m => m.id === action.messageId)
      if (idx === -1) return state
      const next = list.slice()
      next[idx] = { ...next[idx], content: next[idx].content + action.delta }
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [action.conversationId]: next,
        },
      }
    }

    case 'UPDATE_MESSAGE_STATUS': {
      const list = state.messagesByConversation[action.conversationId]
      if (!list) return state
      const idx = list.findIndex(m => m.id === action.messageId)
      if (idx === -1) return state
      const next = list.slice()
      const msg = next[idx]
      next[idx] = {
        ...msg,
        status: action.status,
        finishedAt:
          action.status === 'success' || action.status === 'failed'
            ? new Date().toISOString()
            : msg.finishedAt,
      }
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [action.conversationId]: next,
        },
      }
    }

    case 'SET_MESSAGES':
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [action.conversationId]: action.messages,
        },
      }

    case 'APPEND_TOOL_STEP': {
      const steps = state.toolStepsByMessage[action.messageId] ?? []
      return {
        ...state,
        toolStepsByMessage: {
          ...state.toolStepsByMessage,
          [action.messageId]: [...steps, action.step],
        },
      }
    }

    case 'UPDATE_TOOL_STEP': {
      const steps = state.toolStepsByMessage[action.messageId]
      if (!steps) return state
      const idx = steps.findIndex(s => s.stepId === action.stepId)
      if (idx === -1) return state
      const next = steps.slice()
      next[idx] = { ...next[idx], ...action.patch }
      return {
        ...state,
        toolStepsByMessage: {
          ...state.toolStepsByMessage,
          [action.messageId]: next,
        },
      }
    }

    case 'SET_STREAM_STATUS':
      return { ...state, streamStatus: action.status }

    case 'SET_LAST_SEQ':
      return { ...state, lastSeq: action.seq }

    case 'SET_HEARTBEAT':
      return { ...state, lastHeartbeatAt: action.ts }

    case 'INCREMENT_RECONNECT':
      return { ...state, reconnectAttempt: state.reconnectAttempt + 1 }

    case 'RESET_RECONNECT':
      return { ...state, reconnectAttempt: 0 }

    case 'SET_THEME':
      return { ...state, themeMode: action.mode }

    case 'SET_PRIMARY_COLOR':
      return { ...state, primaryColor: action.color }

    case 'SET_ERROR':
      return {
        ...state,
        hasError: action.error !== null,
        lastError: action.error,
      }

    case 'HYDRATE':
      return { ...state, ...action.state }

    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// Serialization / Deserialization
// ---------------------------------------------------------------------------

export interface SerializedState {
  version: number
  state: Omit<
    AssistantState,
    | 'connection'
    | 'turnPhase'
    | 'streamStatus'
    | 'lastSeq'
    | 'lastHeartbeatAt'
    | 'reconnectAttempt'
    | 'hasError'
    | 'lastError'
  >
}

const SERIAL_VERSION = 2

/** Serialize state to JSON-safe object (excludes transient stream fields). */
export function serialize(state: AssistantState): SerializedState {
  const {
    connection,
    turnPhase,
    streamStatus,
    lastSeq,
    lastHeartbeatAt,
    reconnectAttempt,
    hasError,
    lastError,
    ...persistable
  } = state
  void connection
  void turnPhase
  void streamStatus
  void lastSeq
  void lastHeartbeatAt
  void reconnectAttempt
  void hasError
  void lastError
  return { version: SERIAL_VERSION, state: persistable as SerializedState['state'] }
}

/** Deserialize and merge with fresh initial state defaults. */
export function deserialize(data: unknown): AssistantState | null {
  if (!data || typeof data !== 'object') return null
  const parsed = data as SerializedState
  if (parsed.version === 1) {
    const legacy = (parsed as unknown as { state?: Record<string, unknown> }).state
    if (!legacy) return null
    const mode = typeof legacy.mode === 'string' ? (legacy.mode as AiOpenMode) : 'floating'
    const canonical = canonicalPresentationFromLegacy(mode)
    const {
      visible,
      mode: _mode,
      previousMode,
      ...remaining
    } = legacy
    void _mode
    const previous =
      typeof previousMode === 'string'
        ? canonicalPresentationFromLegacy(previousMode as AiOpenMode).preferredPresentation
        : null
    return createInitialState({
      ...(remaining as Partial<AssistantState>),
      visibility: visible === false ? 'closed' : canonical.visibility,
      preferredPresentation: canonical.preferredPresentation,
      resolvedPresentation: canonical.preferredPresentation,
      previousPresentation: previous,
    })
  }
  if (parsed.version !== SERIAL_VERSION) return null
  return createInitialState(parsed.state)
}

/** Convenience: serialize to JSON string. */
export function serializeToString(state: AssistantState): string {
  return JSON.stringify(serialize(state))
}

/** Convenience: deserialize from JSON string. */
export function deserializeFromString(json: string): AssistantState | null {
  try {
    return deserialize(JSON.parse(json))
  } catch {
    return null
  }
}
